/*
 * ANANDA 3D Carousel — vanilla JS, no library deps for the core ring,
 * optional GSAP+ScrollTrigger for the scroll-bound Y-radius bend.
 *
 * Geometry model (per item i):
 *   baseAngle = i * (360 / count)            // fixed slot in the ring
 *   liveAngle = baseAngle + state.rotation   // after user spins
 *   y = yRadius * (1 - cos(liveAngle))       // dome bend
 *
 * Active-item Ken Burns: an .is-active class is toggled onto the item
 * closest to angle 0 each frame; CSS animates a slow translate + scale
 * loop on its image + reflection, so the "hero" image at center feels
 * alive while side items stay still.
 *
 * Click-to-snap (Roi 2026-05-31 round 4 fix):
 *   The per-item click handler we used initially worked on mobile (touch)
 *   but silently failed on desktop (mouse). Root cause: pointerdown calls
 *   stage.setPointerCapture, which redirects subsequent mouse events to
 *   the stage. The synthetic click event then fires on the stage (or the
 *   nearest common ancestor of mousedown/mouseup) instead of on the
 *   clicked .item, so item.addEventListener('click', …) never ran.
 *
 *   Fix: skip click events entirely. In pointerdown, remember which item
 *   was under the pointer (`pendingClickItem`). In release(), if the
 *   user barely moved (moved <= 8), snap to that item. Same code path
 *   for desktop and mobile — no reliance on pointer-capture-aware click
 *   target rules.
 *
 * Interactions:
 *   - Pointer drag horizontally  → rotate the ring (live, no easing).
 *   - Tap/click on a side item   → snap-tween that item to the front.
 *   - Scroll                     → GSAP scrubs `state.yRadius` to target.
 *   - Idle (no user gesture yet) → slow auto-rotate, killed on first interaction.
 */
(function () {
  function init() {
    var root = document.querySelector('[data-ananda-3d-carousel]');
    if (!root || root.dataset.init === '1') return;
    root.dataset.init = '1';

    var stage = root.querySelector('[data-3dc-stage]');
    var ring  = root.querySelector('[data-3dc-ring]');
    var items = ring ? ring.querySelectorAll('[data-3dc-item]') : [];
    if (!stage || !ring || items.length < 2) return;

    var COUNT = items.length;
    var ANGLE_STEP = 360 / COUNT;

    var xRadiusDesktop = parseFloat(root.dataset.xRadiusDesktop) || 440;
    var xRadiusMobile  = parseFloat(root.dataset.xRadiusMobile)  || 260;
    var yRadiusTarget  = parseFloat(root.dataset.yRadiusTarget)  || -60;
    var autoplayOn     = root.dataset.autoplay !== 'false';

    var state = {
      rotation: 0,
      yRadius : 0,
    };
    var stoppedByUser = false;
    var autoTimer = null;
    var lastFrameId = null;
    var snapAnimId = null;
    var activeIdx = -1;
    var prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function currentXRadius() {
      return window.innerWidth >= 750 ? xRadiusDesktop : xRadiusMobile;
    }

    function layout() {
      var xR = currentXRadius();
      for (var i = 0; i < COUNT; i++) {
        var item = items[i];
        var baseAngle = i * ANGLE_STEP;
        item.style.setProperty('--c3d-base-angle', baseAngle + 'deg');
      }
      ring.style.setProperty('--c3d-x-radius', xR + 'px');
    }

    function apply() {
      var xR = currentXRadius();
      ring.style.transform =
        'translateZ(' + (-xR) + 'px) rotateY(' + state.rotation + 'deg)';

      var yR = state.yRadius;
      var bestAbsAngle = Infinity;
      var nextActive = 0;

      for (var i = 0; i < COUNT; i++) {
        var item = items[i];
        var baseAngle = i * ANGLE_STEP;
        var liveAngleDeg = baseAngle + state.rotation;
        var rad = liveAngleDeg * Math.PI / 180;
        var cosA = Math.cos(rad);

        var yOffset = yR * (1 - cosA);

        item.style.transform =
          'rotateY(' + baseAngle + 'deg) ' +
          'translateZ(' + xR + 'px) ' +
          'translateY(' + yOffset.toFixed(2) + 'px)';

        var depth = (cosA + 1) / 2;
        item.style.opacity = (0.35 + 0.65 * depth).toFixed(3);

        var normalized = ((liveAngleDeg % 360) + 540) % 360 - 180;
        var absAngle = Math.abs(normalized);
        if (absAngle < bestAbsAngle) {
          bestAbsAngle = absAngle;
          nextActive = i;
        }
      }

      if (nextActive !== activeIdx) {
        if (activeIdx >= 0 && items[activeIdx]) {
          items[activeIdx].classList.remove('is-active');
        }
        activeIdx = nextActive;
        items[activeIdx].classList.add('is-active');
      }
    }

    function scheduleApply() {
      if (lastFrameId) return;
      lastFrameId = requestAnimationFrame(function () {
        lastFrameId = null;
        apply();
      });
    }

    /* Snap-tween: rotate the ring so that item `i` lands at angle 0
     * (front-center). Uses shortest wrap path so we never spin the
     * long way around. Cubic ease-out, 600ms duration. */
    function snapToIndex(i) {
      if (snapAnimId) cancelAnimationFrame(snapAnimId);
      var targetRotation = -i * ANGLE_STEP;
      var delta = ((targetRotation - state.rotation) % 360 + 540) % 360 - 180;
      if (Math.abs(delta) < 0.5) return; // already there
      var startRot = state.rotation;
      var startTime = performance.now();
      var dur = 600;
      function tick(now) {
        var t = Math.min(1, (now - startTime) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        state.rotation = startRot + delta * eased;
        apply();
        if (t < 1) snapAnimId = requestAnimationFrame(tick);
        else snapAnimId = null;
      }
      snapAnimId = requestAnimationFrame(tick);
    }

    layout();
    apply();

    /* ---- Drag-to-rotate + click-to-snap (unified pointer path) ---- */
    var dragging = false, startX = 0, startRot = 0, moved = 0;
    var pendingClickItem = null;

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = e.clientX;
      startRot = state.rotation;
      // Walk up from the actual event target to find the .item that
      // was under the pointer at press time. We'll use this in release()
      // to decide whether the gesture was a tap-to-snap or a drag.
      // (.frame is the closest event-receiving ancestor inside .item,
      // since .img is pointer-events: none.)
      pendingClickItem = e.target && e.target.closest
        ? e.target.closest('[data-3dc-item]')
        : null;
      stage.classList.add('is-dragging');
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      stopAutoForGood();
      e.preventDefault();
    });

    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      moved = Math.abs(dx);
      state.rotation = startRot + dx * 0.3;
      scheduleApply();
    });

    function release() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');

      // Tap-to-snap path: if the pointer barely moved AND there was an
      // item under it at press time, animate that item to the front.
      // Works identically on desktop (mouse) and mobile (touch) because
      // we never depend on the synthetic click event (which is broken
      // by setPointerCapture on desktop).
      if (moved <= 8 && pendingClickItem) {
        var i = Array.prototype.indexOf.call(items, pendingClickItem);
        if (i !== -1) snapToIndex(i);
      }
      pendingClickItem = null;
    }

    stage.addEventListener('pointerup',     release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('pointerleave',  release);

    /* Suppress legacy synthetic clicks on the stage (e.g. nested links
     * inside images) for drag gestures so they don't trigger navigation
     * after a real drag. Tap-to-snap is now handled in release() above,
     * not via click. */
    stage.addEventListener('click', function (e) {
      if (moved > 8) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* ---- Scroll-bound dome bend via GSAP ---- */
    if (!prefersReducedMotion && window.gsap && window.ScrollTrigger) {
      window.gsap.to(state, {
        yRadius: yRadiusTarget,
        ease: 'none',
        scrollTrigger: {
          trigger: root,
          start: 'top 85%',
          end:   'bottom 30%',
          scrub: 1.2,
          onUpdate: scheduleApply,
        },
      });
    }

    /* ---- Idle auto-rotate ---- */
    function startAutoLoop() {
      if (stoppedByUser || prefersReducedMotion || !autoplayOn) return;
      if (autoTimer) return;
      autoTimer = setInterval(function () {
        if (dragging) return;
        state.rotation += 0.3;
        scheduleApply();
      }, 50);
    }
    function stopAutoForGood() {
      stoppedByUser = true;
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }
    setTimeout(startAutoLoop, 1500);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && autoTimer) {
        clearInterval(autoTimer); autoTimer = null;
      } else if (!document.hidden && !stoppedByUser) {
        startAutoLoop();
      }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { layout(); apply(); }, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
